import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { radius } from '@/theme/tokens.generated';

// TEMPORARY diagnostic tool for issue #297's 8th recurrence (2026-09-18):
// every prior fix (safe-area padding floors x6, justifyContent:center,
// 100dvh) was guessed from a sandbox that cannot run real Safari, and
// each one either did nothing or broke something else on the founder's
// real device. Rather than guess a 9th time, this renders LIVE measured
// numbers (not assumptions) on the founder's own phone: the raw
// env(safe-area-inset-bottom) value, visualViewport vs innerHeight (do
// they track Safari's toolbar show/hide at all on this device?), and
// the actual tab bar's own getBoundingClientRect + computed padding.
// Gated behind ?debug=tabbar so it never renders in the normal product
// -- DELETE this file and its one call site in (app)/_layout.tsx once
// #297 is confirmed fixed on a real device.
export function TabBarDebugOverlay() {
  const [lines, setLines] = useState<string[]>(['measuring...']);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;

    const probe = document.createElement('div');
    probe.style.position = 'fixed';
    probe.style.bottom = '0';
    probe.style.left = '0';
    probe.style.width = '0';
    probe.style.height = '0';
    probe.style.paddingBottom = 'env(safe-area-inset-bottom)';
    probe.style.paddingTop = 'env(safe-area-inset-top)';
    probe.style.visibility = 'hidden';
    document.body.appendChild(probe);

    function findTabBar(): HTMLElement | null {
      const all = document.querySelectorAll('*');
      let label: Element | null = null;
      for (const el of all) {
        if (el.children.length === 0 && ['Threads', 'Matches', 'Profile'].includes((el.textContent || '').trim())) {
          label = el;
          break;
        }
      }
      if (!label) return null;
      // Walk up until we find the row that contains all three tab items
      // (width spans most of the viewport, height under 120px -- the
      // tab bar itself, not the full-screen ancestor above it).
      let cur: HTMLElement | null = label as HTMLElement;
      while (cur) {
        const r = cur.getBoundingClientRect();
        if (r.width > window.innerWidth * 0.8 && r.height > 0 && r.height < 140) return cur;
        cur = cur.parentElement;
      }
      return null;
    }

    function sample() {
      const cs = getComputedStyle(probe);
      const bar = findTabBar();
      const barInfo = bar
        ? (() => {
            const r = bar.getBoundingClientRect();
            const bcs = getComputedStyle(bar);
            return `bar rect: top=${r.top.toFixed(0)} bottom=${r.bottom.toFixed(0)} h=${r.height.toFixed(0)} | bar pad: t=${bcs.paddingTop} b=${bcs.paddingBottom} | winH=${window.innerHeight} gapBelowBar=${(window.innerHeight - r.bottom).toFixed(0)}`;
          })()
        : 'bar not found';

      setLines([
        `env(safe-area-inset-bottom)=${cs.paddingBottom} top=${cs.paddingTop}`,
        `visualViewport.height=${window.visualViewport?.height ?? 'n/a'} innerHeight=${window.innerHeight} docClientHeight=${document.documentElement.clientHeight}`,
        `#root computed height=${document.getElementById('root') ? getComputedStyle(document.getElementById('root')!).height : 'n/a'}`,
        barInfo,
        `time=${new Date().toLocaleTimeString()}`,
      ]);
    }

    sample();
    const interval = setInterval(sample, 500);
    window.visualViewport?.addEventListener('resize', sample);
    window.addEventListener('resize', sample);

    return () => {
      clearInterval(interval);
      window.visualViewport?.removeEventListener('resize', sample);
      window.removeEventListener('resize', sample);
      document.body.removeChild(probe);
    };
  }, []);

  if (Platform.OS !== 'web') return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 4,
        left: 4,
        right: 4,
        zIndex: 99999,
        background: 'rgba(0,0,0,0.85)',
        color: '#0f0',
        fontFamily: 'monospace',
        fontSize: 10,
        padding: 8,
        borderRadius: radius.sm,
        pointerEvents: 'none',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.4,
      }}
    >
      {lines.join('\n')}
    </div>
  );
}
